/**
 * Ring while a call this operator placed is ringing at the other end.
 *
 * ONE RING, HERE OR THERE, NEVER BOTH. The Voice SDK plays a sound of its own
 * while an outgoing call rings, and `audio/apply.ts` switches that off exactly
 * when this hook has something to play instead - see `applySdkRingSounds`. The
 * two decisions are one decision and must not drift: silencing the SDK without
 * a file here is a dial that goes quiet, and playing a file without silencing
 * the SDK is the doubled ring the incoming side already fixed once.
 *
 * NOT GATED ON REDUCED MOTION, unlike the incoming ring. That preference means
 * "do not surprise me", and a call the operator placed thirty milliseconds ago
 * by pressing a button is not a surprise. It is also the faithful swap: the
 * SDK's sound played for everybody regardless of the preference, so honouring
 * it here would take a sound AWAY from the operators who never asked for that.
 *
 * Mounted from `CallWindow`, which is on screen for the whole session, rather
 * than from the live call surface - tucking a ringing call into the corner pill
 * unmounts that surface, and a ring that stops when the window is minimised is
 * a ring nobody can trust.
 */
import { useEffect, useRef } from "react";
import { readAudioChoice } from "../audio/choice";
import type { Ring } from "../incoming/ringSpec";
import { startRingback } from "./ringback";
import { hasCustomRingback, ringbackSource } from "./ringbackSource";

export function useRingback(active: boolean): void {
  const ringRef = useRef<Ring | null>(null);

  useEffect(() => {
    if (!active) return;
    // Nothing uploaded: the SDK's own sound is still on and is doing this job.
    if (!hasCustomRingback()) return;

    // The ring can be torn down before it has finished being built - the
    // callee answers on the first ring while `setSinkId` is still in flight -
    // and without the flag that ring would start into a live conversation.
    let cancelled = false;

    // `.output`, the call speaker, NOT `.ring` - see `startRingback` for why the
    // outgoing ring follows the call and the incoming one follows the room.
    void startRingback(ringbackSource(), readAudioChoice().output)
      .then((ring) => {
        if (!ring) return;
        if (cancelled) {
          ring.stop();
          return;
        }
        ringRef.current = ring;
      })
      .catch(() => {
        // No audio device, or a browser refusing to play. The call window is
        // already saying the phone is ringing.
      });

    return () => {
      cancelled = true;
      ringRef.current?.stop();
      ringRef.current = null;
    };
  }, [active]);
}
